/**
 * THE ADDRESS CARRIES THE BASKET (2026-10-25). Renders nothing; mounted once in App.js, inside the router and under
 * CartProvider, so it covers every store page.
 *
 *   · INSIDE AN APP'S BROWSER it writes the basket (and the ad) into the address bar on every basket, route or history
 *     pop (utils/inAppAddress.js; never a token), so the app's own "Open in browser" menu — which opens whatever address the page shows —
 *     brings the basket along. A buyer on 2026-10-21 lost theirs because that address was bare.
 *   · IN A REAL BROWSER it is the arrival seeder: an address with `items=…&handoff=1` is merged into this browser's
 *     basket once (utils/seedHandoffBasket.js) and stripped of the parameters. The checkout route seeds itself — it
 *     also restores a token — so this skips it. Nothing is redirected, ever: the shopper arrives where the address says.
 *
 * Inside an app it NEVER seeds from the address: the address there is this browser's own basket written out, and
 * reading it back would add it on top of itself.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../Context/CartContext';
import { isInAppBrowser } from '../../utils/stableViewport';
import { handoffItemsOf, withoutHandoff } from '../../utils/openInBrowser';
import { keepBasketInAddress } from '../../utils/inAppAddress';
import { currentAdVisit } from '../../utils/adVisit';
import { seedHandoffBasket } from '../../utils/seedHandoffBasket';
import { isDomainMode } from '../../utils/domainMode';
import useRouteSalonSlug from '../../utils/useRouteSalonSlug';

const onCheckoutRoute = (pathname) => /\/checkout\/?$/.test(String(pathname || ''));

/** The shop a platform address names: its first segment (`/dk-store/shop` → `dk-store`). On a business's own domain the host decides instead. */
const slugOfPath = (pathname) => {
  if (isDomainMode()) return null;
  const seg = String(pathname || '').split('/').filter(Boolean)[0];
  return seg || null;
};

export default function InAppBasketAddress({ salonSlug: slugFromProp = null }) {
  const { pathname, search, hash, state: routeState } = useLocation();
  const navigate = useNavigate();
  // The latest location, for the strip that runs after a network fetch (see below).
  const locationRef = useRef(null);
  locationRef.current = { pathname, search, hash, state: routeState };
  // The component sits outside <Routes>, so the route has no `:salonSlug` for it: the path, or the host, says which shop.
  const { salonSlug } = useRouteSalonSlug(slugFromProp || slugOfPath(pathname));
  const { i18n } = useTranslation();
  const cart = useCart(salonSlug || '__none__');
  const inApp = typeof navigator !== 'undefined' && isInAppBrowser(navigator.userAgent);
  const onCheckout = onCheckoutRoute(pathname);
  const [tick, setTick] = useState(0);
  const itemsSignature = cart.items.map((i) => `${i.productId}:${i.variantId}:${i.quantity}`).join(',');
  const cartRef = useRef(cart);
  cartRef.current = cart;

  /* A SWIPE BACK IS A CHANGE THE ROUTER NEVER SEES (2026-10-25, final review). The back-swipe guard (utils/inAppBackGuard)
     answers an edge swipe by popping to the ROOT entry — the address the shopper arrived on, written before any
     basket — and pushing the same address again. Path, search and key are the same for React Router, so no effect
     below would run and the app's "Open in browser" would open a bare address. Every pop inside an app is a reason to
     write again. (The guard's own popstate listener was added first, so it has pushed its entry by the time this runs.) */
  useEffect(() => {
    if (!inApp) return undefined;
    const onPop = () => setTick((n) => n + 1);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [inApp]);

  // INSIDE AN APP: the lines and the ad in the address bar, kept current. Never a token (see utils/inAppAddress.js).
  useEffect(() => {
    if (!inApp) return;
    keepBasketInAddress(window, { items: cartRef.current.items, adVisit: currentAdVisit() });
  }, [inApp, itemsSignature, pathname, search, tick]);

  // IN A REAL BROWSER: the arrival seeder, once per address.
  const seededRef = useRef(null);
  useEffect(() => {
    if (inApp || onCheckout || !salonSlug) return;
    const wanted = handoffItemsOf(search);
    if (!wanted.length || seededRef.current === search) return;
    seededRef.current = search;
    /* THROUGH THE ROUTER, NOT history.replaceState (final review, 2026-10-25): a strip the router never heard of left its
       own search stale, and Shop's filters (useShopFilters) re-applied it, putting items=…&handoff=1 back. */
    const strip = () => {
      try {
        /* THE LOCATION AT STRIP TIME, NOT AT ARRIVAL (re-review, 2026-10-25): the strip runs after a network fetch. A
           shopper who moved to another page meanwhile must stay there; only a location that still carries the
           parameters is stripped, with its own path, hash and state. */
        const now = locationRef.current;
        const q = new URLSearchParams(now.search || '');
        if (!q.has('items') && !q.has('handoff')) return;
        navigate({ pathname: now.pathname, search: withoutHandoff(now.search), hash: now.hash }, { replace: true, state: now.state });
      } catch (_) { /* the address keeps its parameters; the device's memory keeps them from adding twice */ }
    };
    seedHandoffBasket({ salonSlug, items: wanted, cart: cartRef.current, language: i18n && i18n.language })
      .catch(() => 0)
      .then(strip);
  // Once per address: the language is read when it runs, not a reason to run again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inApp, onCheckout, salonSlug, search]);

  return null;
}
